/**
 * Environnement de données du back-office (test / preprod / prod).
 *
 * Source UNIQUE à l'exécution : la valeur du contexte React
 * (`EnvironmentContext`), propre à l'onglet. Le contexte la branche sur le
 * client HTTP commun (`lib/http.ts`), qui pose l'en-tête `X-Environment`.
 * Le localStorage (partagé par tous les onglets) ne sert QUE de valeur
 * initiale : il n'est jamais relu par les requêtes.
 */

export type AppEnvironment = 'test' | 'preprod' | 'prod';

/** Tous les environnements de données. */
export const ENVIRONMENTS: readonly AppEnvironment[] = ['test', 'preprod', 'prod'];

/** Clé du localStorage qui mémorise le dernier choix (valeur initiale). */
export const ENVIRONMENT_STORAGE_KEY = 'suklu_admin_env';

/**
 * Libellés et couleur de pastille de chaque environnement : `label` pour le
 * sélecteur et les bandeaux, `longLabel` pour la cible d'une migration.
 */
export const ENVIRONMENT_META: Record<AppEnvironment, {
  label: string; longLabel: string; color: 'warning' | 'info' | 'error';
}> = {
  test: { label: 'Test', longLabel: 'Test', color: 'warning' },
  preprod: { label: 'Preprod', longLabel: 'Pre-Production', color: 'info' },
  prod: { label: 'Prod', longLabel: 'Production', color: 'error' },
};

/** Vrai si la valeur est un environnement connu. */
export const isAppEnvironment = (value: unknown): value is AppEnvironment =>
  typeof value === 'string' && (ENVIRONMENTS as readonly string[]).includes(value);

/** Défaut historique quand le build ne fixe pas d'environnement. */
const FALLBACK_ENVIRONMENT: AppEnvironment = 'test';

/** Environnements servis par une fonction verrouillée (400 ENV_MISMATCH sinon). */
const LOCKED_BUILD_ENVIRONMENTS: readonly AppEnvironment[] = ['test', 'preprod'];

/**
 * Environnement par défaut du build (`REACT_APP_DEFAULT_ENV`, posé par la
 * CI : develop → test, preprod → preprod, main → prod) ; à défaut 'test'.
 */
export const resolveDefaultEnvironment = (buildValue: string | undefined): AppEnvironment =>
  isAppEnvironment(buildValue) ? buildValue : FALLBACK_ENVIRONMENT;

/**
 * Environnements sélectionnables pour ce build : une console construite
 * contre une fonction verrouillée (test / preprod) ne propose que son
 * environnement ; sinon (prod ou build local) les trois.
 */
export const resolveAvailableEnvironments = (
  buildValue: string | undefined,
): readonly AppEnvironment[] =>
  isAppEnvironment(buildValue) && LOCKED_BUILD_ENVIRONMENTS.includes(buildValue)
    ? [buildValue]
    : ENVIRONMENTS;

/** Environnement retenu quand rien n'est mémorisé (premier lancement). */
export const DEFAULT_ENVIRONMENT: AppEnvironment =
  resolveDefaultEnvironment(process.env.REACT_APP_DEFAULT_ENV);

/** Environnements proposés par le sélecteur de l'en-tête. */
export const AVAILABLE_ENVIRONMENTS: readonly AppEnvironment[] =
  resolveAvailableEnvironments(process.env.REACT_APP_DEFAULT_ENV);

/**
 * Environnement mémorisé au dernier choix (valeur INITIALE d'un onglet).
 * Valeur mémorisée si elle est proposée par ce build, sinon
 * `DEFAULT_ENVIRONMENT`. Lu une seule fois, au montage du contexte.
 */
export const readStoredEnvironment = (): AppEnvironment => {
  const stored = localStorage.getItem(ENVIRONMENT_STORAGE_KEY);
  return isAppEnvironment(stored) && AVAILABLE_ENVIRONMENTS.includes(stored)
    ? stored
    : DEFAULT_ENVIRONMENT;
};

/** Mémorise l'environnement choisi (défaut des prochains onglets). */
export const storeSelectedEnvironment = (env: AppEnvironment): void => {
  localStorage.setItem(ENVIRONMENT_STORAGE_KEY, env);
};
